import type { ReactNode } from "react";
import { BOOKING_URL, contact, navigation } from "../models/site";
import { useMenuPresenter } from "../presenters/useInteractionPresenters";
import { GoogleFooterMap } from "./GoogleFooterMap";
import { SmartImage } from "./SmartImage";

export function Header() {
  const menu = useMenuPresenter();
  return (
    <header className="site-header">
      <a className="skip-link" href="#main">
        Skip to Content
      </a>
      <div className="header-inner">
        <a className="brand" href="/" aria-label="都蘭小星民宿首頁">
          <SmartImage
            name="logo"
            alt="都蘭小星民宿 Dulan Star Guest House"
            eager
            width={1500}
            height={309}
            sizes="(max-width: 767px) 168px, (max-width: 1240px) 190px, 250px"
          />
        </a>
        <button
          className="menu-toggle"
          type="button"
          aria-expanded={menu.open}
          aria-controls="site-navigation"
          onClick={menu.toggle}
        >
          <span aria-hidden="true">{menu.open ? "×" : "☰"}</span>
          <span className="sr-only">
            {menu.open ? "Close Menu" : "Open Menu"}
          </span>
        </button>
        <nav
          id="site-navigation"
          className={menu.open ? "site-nav is-open" : "site-nav"}
          aria-label="主要導覽"
        >
          {navigation.map((item) => (
            <a key={item.href} href={item.href} onClick={menu.close}>
              | {item.zh} {item.en} |
            </a>
          ))}
        </nav>
        <a
          className="book-button"
          href={BOOKING_URL}
          target="_blank"
          rel="noreferrer"
        >
          BOOK
        </a>
      </div>
    </header>
  );
}

function SocialLinks() {
  return (
    <div className="social-links" aria-label="社群連結">
      <a
        href={contact.instagram}
        target="_blank"
        rel="noreferrer"
        aria-label="Instagram"
      >
        <img src="/instagram.svg" alt="" width="28" height="28" />
      </a>
      <a
        href={contact.facebook}
        target="_blank"
        rel="noreferrer"
        aria-label="Facebook"
      >
        <img src="/facebook.svg" alt="" width="28" height="28" />
      </a>
    </div>
  );
}

function ContactIcon({ type }: { type: "email" | "phone" }) {
  if (type === "email") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="1.5" />
        <path d="m4 7 8 6 8-6" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8.1 3.8 5.4 5.1c-.8.4-1.2 1.3-.9 2.2 2 5.8 6.5 10.3 12.3 12.3.9.3 1.8-.1 2.2-.9l1.3-2.7-4.1-2-1.3 2.1a13.3 13.3 0 0 1-7-7L10.1 8l-2-4.2Z" />
    </svg>
  );
}

export function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-inner">
        <section>
          <h2>Contact Us</h2>
          <div className="footer-rule" />
          <address className="contact-details">
            <a className="contact-detail" href={`tel:${contact.phone}`}>
              <span className="contact-icon">
                <ContactIcon type="phone" />
              </span>
              <span className="sr-only">電話：</span>
              <span className="contact-value">{contact.phoneDisplay}</span>
            </a>
            <a className="contact-detail" href={`mailto:${contact.email}`}>
              <span className="contact-icon">
                <ContactIcon type="email" />
              </span>
              <span className="sr-only">Email：</span>
              <span className="contact-value">{contact.email}</span>
            </a>
            <a
              className="contact-detail"
              href={contact.line}
              target="_blank"
              rel="noreferrer"
            >
              <span className="contact-icon">
                <img src="/line.svg" alt="" width="21" height="21" />
              </span>
              <span className="sr-only">LINE：</span>
              <span className="contact-value">{contact.lineId}</span>
            </a>
          </address>
        </section>
        <section>
          <h2>Quick Links</h2>
          <div className="footer-rule" />
          <a className="outline-link" href="/rooms">
            房間 ROOMS
          </a>
          <a className="outline-link" href="/direction">
            位置 DIRECTION
          </a>
          <a className="outline-link" href="/events">
            活動 EVENTS
          </a>
        </section>
        <section>
          <h2>Follow Us</h2>
          <div className="footer-rule" />
          <SocialLinks />
        </section>
        <GoogleFooterMap />
      </div>
      <p className="copyright">
        © {new Date().getFullYear()} Dulan Star Guest House
      </p>
      <p className="footer-legal">
        <a href="/privacy">個人資料蒐集告知</a>
      </p>
    </footer>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <>
      <Header />
      <main id="main">{children}</main>
      <Footer />
    </>
  );
}
